import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '../adminLayout';
import { IntlMessages } from '../../../helper/langHelper';
import { getProcedures, toggleProcedureActivity } from '../../../services/admin/procedures';
import { UsersContainer, UsersHeader, LoaderWrapper, IconButton } from '../users/Users.styles';
import { Table } from '../../../components/uielements/table';
import { PrimaryButton } from '../users/modals/UserPromoteModal.styles';
import CreateAndEditProcedureModal from './modals/Createandeditproceduremodal';
import { Switch } from '../../../components/uielements';

const Procedures = () => {
  const [page, setPage] = useState(0);
  const [perPage] = useState(10);
  const [editProcedureData, setEditProcedureData] = useState(null);
  const [addProcedureData, setAddProcedureData] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['adminProcedures', page, perPage],
    queryFn: () => getProcedures(page, perPage),
  });

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload) => toggleProcedureActivity(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['adminProcedures'] });
    },
    onError: (error) => {
      alert(`Error: ${error?.response?.data?.message}`);
    },
  });

  const tableData = data?.data ?? [];
  const total = data?.total ?? 0;

  const handleToggleProcedure = (procedureId) => {
    mutation.mutate({ procedureId });
  };

  const columns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.name" defaultMessage="Procedure Name" />,
        accessor: 'procedureName',
        width: '25%',
      },
      {
        Header: <IntlMessages id="table.col.definition" defaultMessage="Definition" />,
        accessor: 'defination',
        width: '60%',
      },

      {
        Header: <IntlMessages id="table.col.actions" defaultMessage="Actions" />,
        accessor: 'actions',
        isAction: true,
        width: '15%',
      },
    ],
    []
  );

  const actionList = [
    (data) => (
      <IconButton onClick={() => setEditProcedureData(data)} variant="promote">
        <span style={{ fontSize: '13px', padding: '0 6px' }}>Edit</span>
      </IconButton>
    ),
    (data) => {
      return (
        <Switch
          checked={data?.isActive || false}
          onClick={() => {
            handleToggleProcedure(data?._id);
          }}
          style={{ width: '20px', height: '18px' }}
        />
      );
    },
  ];

  if (isLoading) {
    return (
      <AdminLayout pageTitle="Procedures" activeTab="procedures">
        <LoaderWrapper>Loading...</LoaderWrapper>
      </AdminLayout>
    );
  }

  if (isError) {
    return (
      <AdminLayout pageTitle="Procedures" activeTab="procedures">
        <LoaderWrapper>Oops! Error Encountered</LoaderWrapper>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="procedures">
      <UsersContainer>
        <UsersHeader>
          <IntlMessages id="nav.procedures" defaultMessage="Procedures" />
          <PrimaryButton onClick={() => setAddProcedureData(true)}>Add Procedure</PrimaryButton>
        </UsersHeader>

        <Table
          data={tableData}
          columns={columns}
          hasFooter={true}
          actions={actionList}
          page={page}
          pageSize={perPage}
          prevPage={() => setPage((p) => Math.max(0, p - 1))}
          nextPage={() => setPage((p) => p + 1)}
          total={total}
        />

        {editProcedureData && (
          <CreateAndEditProcedureModal
            onClose={() => setEditProcedureData(null)}
            isEdit={true}
            procedureId={editProcedureData._id}
            initialData={{
              procedureName: editProcedureData.procedureName,
              defination: editProcedureData.defination,
              pdfArray: editProcedureData.pdfArray || [],
            }}
          />
        )}
        {addProcedureData && (
          <CreateAndEditProcedureModal
            onClose={() => setAddProcedureData(false)}
            isEdit={false}
            procedureId={null}
          />
        )}
      </UsersContainer>
    </AdminLayout>
  );
};

export default Procedures;
